import { datadogRum } from '@datadog/browser-rum';
import type { MentiError } from '@mentimeter/error-utils/error';
import {
  addBreadcrumb as sentryAddBreadcrumb,
  captureException as sentryCaptureException,
  captureMessage as sentryCaptureMessage,
} from '@sentry/nextjs';

/**
 * Captures an exception event and sends it to our monitoring providers.
 */
export function captureException(
  ...args: Parameters<typeof sentryCaptureException>
) {
  const [ex, hint] = args;

  if (
    process.env.NODE_ENV === 'development' &&
    process.env.NEXT_PUBLIC_CAPTURE_EXCEPTION_LOG_LEVEL === 'verbose' &&
    ex
  ) {
    const params = [ex];
    if (hint) {
      params.push(hint);
    }

    // eslint-disable-next-line no-console
    console.warn('[Mentimeter] Captured exception:', ...params);
  }

  if (ex) {
    const error: MentiError = ex;
    const { feature, tags } = error;

    /**
     * @see https://docs.datadoghq.com/real_user_monitoring/browser/collecting_browser_errors/#collect-errors-manually
     */
    datadogRum.addError(error, {
      feature,
      tags,
      hint,
    });
  }

  return sentryCaptureException(...args);
}

/**
 * Captures a message event and sends it to our monitoring providers.
 */
export function captureMessage(
  ...args: Parameters<typeof sentryCaptureMessage>
) {
  const [message, captureContext] = args;

  /**
   * @see https://docs.datadoghq.com/real_user_monitoring/browser/collecting_browser_errors/#collect-errors-manually
   */
  datadogRum.addError(message, {
    context: captureContext,
  });

  return sentryCaptureMessage(...args);
}

/**
 * Records a new breadcrumb which will be attached to future events.
 *
 * Breadcrumbs will be added to subsequent events to provide more context
 * on user's actions prior to an error or crash.
 */
export function addBreadcrumb(...args: Parameters<typeof sentryAddBreadcrumb>) {
  const [breadcrumb, hint] = args;

  const { message, ...rest } = breadcrumb;

  /**
   * @see https://docs.datadoghq.com/real_user_monitoring/guide/send-rum-custom-actions/
   */
  datadogRum.addAction(message ?? 'Action', {
    hint,
    ...rest,
  });

  return sentryAddBreadcrumb(...args);
}
